<script setup lang="ts">
import { ExclamationIcon } from '~/components/design-system/icons';
import Button from '~/components/design-system/button.vue';
import { ChevronDownIcon } from '@heroicons/vue/24/outline';
import ExternalIcon from '../design-system/icons/ExternalIcon.vue';
import LoaderCircle from '../user-interface/loaders/loader-circle.vue';

interface IUserBalanceProps {
  amount?: number;
  label: string;
  error?: boolean;
  hideBalance?: boolean;
  loading?: boolean;
  toggleBalanceModal?: boolean;
  toggleRefreshBalances?: boolean;
  isAccountModal?: boolean;
  backgroundColor?: 'bg-base-secondary' | 'bg-base';
  singleLine?: boolean;
  truncateBalance?: boolean;
  isRewardsBalance?: boolean;
}

const props = withDefaults(defineProps<IUserBalanceProps>(), {
  amount: 0,
  error: false,
  hideBalance: false,
  loading: false,
  toggleBalanceModal: false,
  refreshBalances: false,
  isAccountModal: false,
  backgroundColor: 'bg-base-secondary',
  singleLine: false,
  truncateBalance: false,
  isRewardsBalance: false,
});

const siteStore = useSiteStore();
const { $walletService } = useServices();

const emit = defineEmits<{ (e: 'refresh-balances'): void }>();

const formattedAmount = computed(() =>
  useFormatCurrency(
    props.amount,
    props.truncateBalance,
    '',
    !props.isRewardsBalance,
  ),
);
</script>

<template>
  <div
    class="text-xs flex items-center justify-between h-10 text-base-priority px-1 relative overflow-hidden min-w-max w-full"
    :class="[
      { 'min-w-[40vw] sm:min-w-[15vw]': props.isAccountModal },
      !isAccountModal && backgroundColor,
      isAccountModal && `${backgroundColor} sm:bg-layer`,
    ]"
  >
    <div
      v-if="loading"
      class="absolute w-full h-full flex items-center justify-center backdrop-blur-sm top-0 left-0 z-2"
    >
      <LoaderCircle />
    </div>
    <div
      :class="[
        'p-1',
        'font-bold',
        { 'flex justify-between w-full gap-1.5': singleLine },
      ]"
    >
      <div class="truncate capitalize text-xxs">
        {{ props.label }}
      </div>
      <div v-if="error" class="flex justify-center">
        <ExclamationIcon
          class="text-primary-gold-500 w-4"
          tooltip="could-not-retrieve-balances"
        />
      </div>
      <div
        v-else-if="!hideBalance"
        class="flex items-center"
        :class="{ '-translate-x-1': isRewardsBalance }"
      >
        <DesignSystemIconsJpcCurrency v-if="isRewardsBalance" />
        <div v-html="formattedAmount" />
      </div>
      <div v-else-if="hideBalance">●●●●●</div>
    </div>
    <slot name="suffix-slot" />
    <ExternalIcon
      v-if="toggleRefreshBalances"
      icon-name="refresh"
      class="w-6 cursor-pointer !mx-0"
      @click="emit('refresh-balances')"
    />

    <Button
      v-if="toggleBalanceModal"
      type="transparent"
      padding="sm"
      icon
      @click="[
        siteStore.activateModal('accountBalances'),
        $walletService.fetchBalances(),
      ]"
    >
      <ChevronDownIcon class="!w-4" />
    </Button>
  </div>
</template>
